// src/components/AssignEmployee.js

import React, { useEffect, useState } from 'react';
import { db } from '../../firebaseConfig';
import { collection, onSnapshot, updateDoc, doc, arrayUnion } from 'firebase/firestore';
import { Box, Typography, FormControl, InputLabel, Select, MenuItem, Button, Alert } from '@mui/material';

// Import: TenantContext
import { useTenant } from '../../contexts/TenantContext';

const AssignEmployee = () => {
  const [types, setTypes] = useState([]);
  const [employees, setEmployees] = useState([]);

  const [selectedType, setSelectedType] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState('');

  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  // tenantId aus dem Context
  const { tenantId } = useTenant();

  useEffect(() => {
    if (!tenantId) return;
  
    // Listener für appointmentTypes
    const typesRef = collection(db, 'tenants', tenantId, 'appointmentTypes');
    const unsubscribeTypes = onSnapshot(typesRef, (snapshot) => {
      const typesList = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
      setTypes(typesList);
    }, (error) => {
      console.error('Fehler beim Abrufen der Terminarten:', error);
      setError('Fehler beim Abrufen der Terminarten.');
    });
  
    // Listener für employees
    const employeesRef = collection(db, 'tenants', tenantId, 'employees');
    const unsubscribeEmployees = onSnapshot(employeesRef, (snapshot) => {
      const employeesList = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
      setEmployees(employeesList);
    }, (error) => {
      console.error('Fehler beim Abrufen der Mitarbeiter:', error);
      setError('Fehler beim Abrufen der Mitarbeiter.');
    });
  
    // Cleanup: Listener entfernen
    return () => {
      unsubscribeTypes();
      unsubscribeEmployees();
    };
  }, [tenantId]);
  

  const handleAssign = async () => {
    if (!selectedType || !selectedEmployee) {
      setError('Bitte wählen Sie sowohl eine Terminart als auch einen Mitarbeiter aus.');
      return;
    }

    try {
      // KORREKT: doc(db, 'tenants', tenantId, 'appointmentTypes', selectedType)
      const typeDocRef = doc(db, 'tenants', tenantId, 'appointmentTypes', selectedType);
      await updateDoc(typeDocRef, {
        assignedEmployees: arrayUnion(selectedEmployee)
      });

      setMessage('Mitarbeiter erfolgreich zugeordnet.');
      setError(null);

      // Lokalen State aktualisieren
      setTypes(types.map(type =>
        type.id === selectedType
          ? {
              ...type,
              assignedEmployees: [
                ...(type.assignedEmployees || []),
                selectedEmployee
              ]
            }
          : type
      ));
    } catch (err) {
      console.error(err);
      setError('Fehler beim Zuordnen des Mitarbeiters.');
    }
  };

  return (
    <Box sx={{ p: 4 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>Terminart</InputLabel>
        <Select
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
        >
          <MenuItem value="">
            <em>Wähle eine Terminart</em>
          </MenuItem>
          {types.map(type => (
            <MenuItem key={type.id} value={type.id}>
              {type.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>Mitarbeiter</InputLabel>
        <Select
          value={selectedEmployee}
          onChange={(e) => setSelectedEmployee(e.target.value)}
        >
          <MenuItem value="">
            <em>Wähle einen Mitarbeiter</em>
          </MenuItem>
          {employees.map(emp => (
            <MenuItem key={emp.id} value={emp.id}>
              {emp.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Button
        variant="contained"
        color="primary"
        onClick={handleAssign}
        fullWidth
      >
        Zuweisen
      </Button>
    </Box>
  );
};

export default AssignEmployee;
